/**
 * services/ai/OpenAIProvider.ts
 * OpenAI provider implementation
 *
 * Implements the `AIProvider` interface for OpenAI's chat completions API.
 * Makes direct browser-to-API calls using raw `fetch()` — no SDK dependency.
 *
 * Features:
 * - Non-streaming chat via POST /v1/chat/completions
 * - Streaming chat via SSE with `stream: true`
 * - API key validation via GET /v1/models
 * - Error handling for 401, 429, 500+ responses
 *
 * Work Unit: WU04 - AI Provider Client Implementations
 * Task: T01 - OpenAI Provider Client
 * Sprint: S01 - AI Architecture & Infrastructure Foundation
 * Project: P09 - AI Integration
 */

// Internal absolute imports (type-only)
import type {
  AIChatOptions,
  AIKeyValidationResult,
  AIMessage,
  AIProvider,
  AIResponse,
  AIStreamChunk,
} from '@/types/shared/AI';

// Internal absolute imports (value)
import { createSSEIterable } from '@/utils/sseParser';

// ─── Constants ──────────────────────────────────────────────────────────────

/** OpenAI API base URL */
const OPENAI_API_BASE = 'https://api.openai.com/v1';

/** Default model when none specified in options */
const DEFAULT_MODEL = 'gpt-4o';

// ─── Provider ───────────────────────────────────────────────────────────────

/**
 * OpenAI provider implementation
 *
 * Makes direct browser-to-API calls to OpenAI's REST API.
 * OpenAI supports CORS, so no proxy is needed.
 *
 * @example
 * ```ts
 * const provider = new OpenAIProvider('sk-...');
 * const response = await provider.chat([{ role: 'user', content: 'Hello' }]);
 * console.log(response.content);
 * ```
 */
export class OpenAIProvider implements AIProvider {
  /** Provider identifier */
  readonly provider = 'openai' as const;

  /** API key for authentication */
  private readonly apiKey: string;

  /**
   * Create a new OpenAI provider instance
   *
   * @param apiKey - OpenAI API key (e.g., 'sk-...')
   */
  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Send a non-streaming chat completion request
   *
   * Posts to `/v1/chat/completions` and normalizes the response
   * into the provider-agnostic `AIResponse` shape.
   *
   * @param messages - Conversation messages
   * @param options - Optional chat parameters (model, maxTokens, temperature, systemPrompt)
   * @returns Normalized AI response
   * @throws Error on network failure or non-OK HTTP status
   */
  async chat(messages: AIMessage[], options?: AIChatOptions): Promise<AIResponse> {
    const model = options?.model ?? DEFAULT_MODEL;
    const allMessages = this.buildMessages(messages, options?.systemPrompt);

    const body: Record<string, unknown> = {
      model,
      messages: allMessages,
      stream: false,
    };

    if (options?.maxTokens != null) {
      body['max_tokens'] = options.maxTokens;
    }
    if (options?.temperature != null) {
      body['temperature'] = options.temperature;
    }

    const response = await fetch(`${OPENAI_API_BASE}/chat/completions`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    const data = await response.json() as OpenAIChatResponse;

    return {
      content: data.choices[0]?.message?.content ?? '',
      model: data.model,
      usage: {
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
        totalTokens: data.usage.total_tokens,
      },
      finishReason: data.choices[0]?.finish_reason ?? 'unknown',
    };
  }

  /**
   * Send a streaming chat completion request
   *
   * Posts to `/v1/chat/completions` with `stream: true` and yields
   * `AIStreamChunk` objects as SSE events arrive. The final chunk
   * has `isComplete: true` when the `[DONE]` sentinel is received.
   *
   * @param messages - Conversation messages
   * @param options - Optional chat parameters (model, maxTokens, temperature, systemPrompt)
   * @returns Async iterable of stream chunks
   * @throws Error on network failure or non-OK HTTP status
   */
  async *chatStream(
    messages: AIMessage[],
    options?: AIChatOptions,
  ): AsyncGenerator<AIStreamChunk, void, undefined> {
    const model = options?.model ?? DEFAULT_MODEL;
    const allMessages = this.buildMessages(messages, options?.systemPrompt);

    const body: Record<string, unknown> = {
      model,
      messages: allMessages,
      stream: true,
    };

    if (options?.maxTokens != null) {
      body['max_tokens'] = options.maxTokens;
    }
    if (options?.temperature != null) {
      body['temperature'] = options.temperature;
    }

    const response = await fetch(`${OPENAI_API_BASE}/chat/completions`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    for await (const event of createSSEIterable(response)) {
      // OpenAI sentinel — previous chunk already yielded isComplete: true
      if (event.data === '[DONE]') {
        return;
      }

      const chunk = JSON.parse(event.data) as OpenAIStreamChunk;
      const delta = chunk.choices[0]?.delta;
      const content = delta?.content ?? '';
      const finishReason = chunk.choices[0]?.finish_reason;

      yield {
        content,
        isComplete: finishReason != null,
      };
    }
  }

  /**
   * Validate an API key against OpenAI
   *
   * Makes a lightweight GET request to `/v1/models` to verify the key.
   * Returns `{ valid: true }` for 200, `{ valid: false }` for 401,
   * and throws for unexpected errors.
   *
   * @param apiKey - Plaintext API key to validate
   * @returns Validation result
   */
  async validateKey(apiKey: string): Promise<AIKeyValidationResult> {
    try {
      const response = await fetch(`${OPENAI_API_BASE}/models`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });

      if (response.ok) {
        return { valid: true };
      }

      if (response.status === 401) {
        return { valid: false, code: 'invalid_key', error: 'Invalid API key. Please check and re-enter.' };
      }

      if (response.status === 429) {
        return { valid: false, code: 'rate_limited', error: 'Rate limited — please try again in a moment.' };
      }

      const errorText = await response.text().catch(() => 'Unknown error');
      return { valid: false, code: 'server_error', error: `Validation failed (${response.status}): ${errorText}` };
    } catch (err) {
      return {
        valid: false,
        code: 'network_error',
        error: `Network error: ${err instanceof Error ? err.message : 'Check your internet connection and try again.'}`,
      };
    }
  }

  // ─── Private Helpers ────────────────────────────────────────────────────────

  /**
   * Build request headers for OpenAI API calls
   *
   * @returns Headers with Authorization and Content-Type
   */
  private buildHeaders(): Record<string, string> {
    return {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Build the messages array, prepending system prompt if provided
   *
   * @param messages - User-provided conversation messages
   * @param systemPrompt - Optional system prompt to prepend
   * @returns Messages array ready for the OpenAI API
   */
  private buildMessages(
    messages: AIMessage[],
    systemPrompt?: string,
  ): Array<{ role: string; content: string }> {
    const result: Array<{ role: string; content: string }> = [];

    if (systemPrompt != null) {
      result.push({ role: 'system', content: systemPrompt });
    }

    for (const msg of messages) {
      result.push({ role: msg.role, content: msg.content });
    }

    return result;
  }

  /**
   * Build a human-readable error message from a failed response
   *
   * @param response - Failed fetch Response
   * @returns Error message string
   */
  private async buildErrorMessage(response: Response): Promise<string> {
    const status = response.status;

    if (status === 401) {
      return 'OpenAI API key is invalid or expired';
    }
    if (status === 429) {
      return 'OpenAI rate limit exceeded — please try again later';
    }
    if (status >= 500) {
      return `OpenAI server error (${status}) — please try again later`;
    }

    const body = await response.text().catch(() => '');
    return `OpenAI request failed (${status}): ${body}`;
  }
}

// ─── OpenAI API Response Types (Internal) ───────────────────────────────────

/** OpenAI chat completion response shape */
interface OpenAIChatResponse {
  choices: Array<{
    message: {
      content: string;
    };
    finish_reason: string;
  }>;
  model: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/** OpenAI streaming chunk shape */
interface OpenAIStreamChunk {
  choices: Array<{
    delta: {
      content?: string;
    };
    finish_reason: string | null;
  }>;
}
